import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Siren, Plus, Copy, Download, XCircle, Undo2, Loader2, Eye } from "lucide-react";
import { api, API, apiErr } from "@/lib/api";
import { playPing } from "@/lib/sound";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { ReadMark, TypingDots, LiveComposer, useAutoScroll, fmtTime } from "@/components/live/LiveParts";

const LANGS = ["hu", "en", "de", "fr", "cs"];

function StartDialog({ open, onClose, onCreated }) {
  const [f, setF] = useState({ email: "", report_reference: "", language: "hu" });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const submit = async () => {
    setBusy(true);
    try {
      const r = await api.post("/mod/livechats", f);
      setResult(r.data);
      onCreated(r.data);
      toast[r.data.email_sent ? "success" : "warning"](r.data.email_sent ? "E-mail elküldve" : "Az e-mail nem ment ki — másold ki a linket");
    } catch (e) {
      toast.error(apiErr(e?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };
  const close = () => { setResult(null); setF({ email: "", report_reference: "", language: "hu" }); onClose(); };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent className="border-red-900/60 bg-[#0d0506] text-zinc-100" data-testid="live-start-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 uppercase tracking-[0.15em]"><Siren size={17} className="text-red-500" /> Emergency line indítása</DialogTitle>
          <DialogDescription className="text-zinc-500">A felhasználó e-mailben kap egy titkos linket az élő csevegéshez.</DialogDescription>
        </DialogHeader>
        {result ? (
          <div className="space-y-2" data-testid="live-start-result">
            <p className={`text-[13px] ${result.email_sent ? "text-emerald-300" : "text-amber-300"}`}>{result.email_sent ? "Az e-mail elküldve." : "Az e-mail küldése nem sikerült. Küldd el a linket más úton:"}</p>
            <div className="flex gap-2">
              <Input readOnly value={result.link} className="h-9 border-red-900/60 bg-black font-mono text-[12px]" data-testid="live-start-link" />
              <button data-testid="live-start-copy" onClick={() => { navigator.clipboard.writeText(result.link); toast.success("Link másolva"); }} className="rounded-md border border-red-900/60 px-3 text-red-300 hover:bg-red-950/50"><Copy size={14} /></button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <Input data-testid="live-start-email" type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} placeholder="Felhasználó e-mail címe" className="h-10 border-red-900/60 bg-black text-[13px]" />
            <Input data-testid="live-start-ref" value={f.report_reference} onChange={(e) => setF({ ...f, report_reference: e.target.value })} placeholder="Kapcsolt ügy azonosítója (opcionális), pl. DSA-2026-…" className="h-10 border-red-900/60 bg-black font-mono text-[13px]" />
            <div className="flex gap-1">
              {LANGS.map((l) => <button key={l} data-testid={`live-start-lang-${l}`} onClick={() => setF({ ...f, language: l })} className={`rounded px-2.5 py-1 text-[11px] font-bold uppercase ${f.language === l ? "bg-red-600 text-white" : "border border-red-900/60 text-zinc-400"}`}>{l}</button>)}
              <span className="ml-2 self-center text-[11px] text-zinc-500">e-mail nyelve</span>
            </div>
          </div>
        )}
        <DialogFooter>
          {result ? <button data-testid="live-start-done" onClick={close} className="rounded-md bg-red-600 px-4 py-2 text-[13px] font-semibold text-white">Kész</button> : (
            <button data-testid="live-start-submit" onClick={submit} disabled={busy || !/\S+@\S+\.\S+/.test(f.email)} className="inline-flex items-center gap-2 rounded-md bg-red-600 px-4 py-2 text-[13px] font-semibold text-white hover:bg-red-500 disabled:opacity-40">
              {busy && <Loader2 size={14} className="animate-spin" />} Indítás
            </button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ChatPanel({ id, onChanged }) {
  const [c, setC] = useState(null);
  const seen = useRef(null);
  const lastTyping = useRef(0);
  const scrollRef = useAutoScroll(c?.messages?.length);

  useEffect(() => {
    let alive = true;
    seen.current = null;
    const tick = () => api.get(`/mod/livechats/${id}`).then((r) => {
      if (!alive) return;
      const n = r.data.messages.filter((m) => m.sender === "user").length;
      if (seen.current !== null && n > seen.current) playPing();
      seen.current = n;
      setC(r.data);
    }).catch(() => {});
    tick();
    const t = setInterval(tick, 1000);
    return () => { alive = false; clearInterval(t); };
  }, [id]);

  if (!c) return <div className="grid flex-1 place-items-center"><Loader2 className="animate-spin text-red-500" /></div>;
  const open = c.status === "open";
  const onType = () => {
    if (Date.now() - lastTyping.current > 2000) { lastTyping.current = Date.now(); api.post(`/mod/livechats/${id}/typing`).catch(() => {}); }
  };
  const send = async (text) => { const r = await api.post(`/mod/livechats/${id}/messages`, { text }); setC((x) => ({ ...x, messages: [...x.messages, r.data] })); };
  const retract = async (m) => { await api.post(`/mod/livechats/${id}/messages/${m.id}/retract`); toast.success("Üzenet visszavonva"); };
  const close = async () => { await api.post(`/mod/livechats/${id}/close`); toast.success("Beszélgetés lezárva"); onChanged(); };
  const pdf = async () => {
    const r = await api.get(`/mod/livechats/${id}/pdf`, { responseType: "blob" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(r.data);
    a.download = `emergency-line-${id.slice(0, 8)}.pdf`;
    a.click();
  };

  return (
    <div className="flex min-w-0 flex-1 flex-col" data-testid="live-panel">
      <div className="flex flex-wrap items-center gap-3 border-b border-red-900/40 px-4 py-3">
        <span className={`h-2.5 w-2.5 rounded-full ${c.user_online ? "bg-red-500 shadow-[0_0_10px_#ef4444]" : "bg-zinc-600"}`} data-testid="live-user-online" />
        <div className="min-w-0">
          <div className="truncate text-[14px] font-semibold text-zinc-100">{c.email}</div>
          <div className="text-[11px] text-zinc-500">{c.report?.reference || "nincs kapcsolt ügy"} · {open ? "nyitott" : `lezárva ${new Date(c.closed_at).toLocaleString("hu-HU", { hour12: false })}`}</div>
        </div>
        <div className="ml-auto flex gap-2">
          <button data-testid="live-copy-link" onClick={() => { navigator.clipboard.writeText(c.link); toast.success("Link másolva"); }} className="inline-flex items-center gap-1 rounded-md border border-zinc-800 px-2.5 py-1.5 text-[12px] text-zinc-300 hover:bg-zinc-900"><Copy size={13} /> Link</button>
          <button data-testid="live-pdf" onClick={pdf} className="inline-flex items-center gap-1 rounded-md border border-zinc-800 px-2.5 py-1.5 text-[12px] text-zinc-300 hover:bg-zinc-900"><Download size={13} /> PDF</button>
          {open && <button data-testid="live-close" onClick={close} className="inline-flex items-center gap-1 rounded-md border border-red-700/60 px-2.5 py-1.5 text-[12px] text-red-300 hover:bg-red-950/50"><XCircle size={13} /> Lezárás</button>}
        </div>
      </div>
      <div ref={scrollRef} className="min-h-0 flex-1 space-y-2.5 overflow-y-auto bg-[#080304] p-4" data-testid="live-mod-messages">
        {c.messages.map((m) => {
          const mine = m.sender === "agent";
          return (
            <div key={m.id} className={`group flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`live-mod-msg-${m.id}`}>
              <div className={`max-w-[75%] rounded-xl px-3.5 py-2 ${m.retracted ? "border border-dashed border-zinc-700 opacity-60" : mine ? "border border-red-500/40 bg-red-600/15" : "border border-zinc-800 bg-zinc-900"}`}>
                <div className="mb-0.5 flex items-center gap-2 text-[10px] font-semibold uppercase tracking-wider text-zinc-500">
                  {mine ? m.author : "Felhasználó"}
                  {mine && !m.retracted && open && <button data-testid={`live-retract-${m.id}`} onClick={() => retract(m)} className="ml-auto inline-flex items-center gap-1 normal-case opacity-50 hover:text-red-300 group-hover:opacity-100"><Undo2 size={11} /> Visszavonás</button>}
                </div>
                <div className={`whitespace-pre-wrap text-[13px] ${m.retracted ? "text-zinc-500 line-through" : "text-zinc-100"}`}>{m.text}</div>
                <div className="mt-0.5 flex items-center justify-end gap-1 font-mono text-[10px] text-zinc-500">
                  {m.retracted && <span className="mr-auto text-red-300/80">visszavonva · {m.retracted_by}</span>}
                  {fmtTime(m.created_at)} {mine && <ReadMark m={m} />}
                </div>
              </div>
            </div>
          );
        })}
        {c.user_typing && (
          <div className="flex justify-start" data-testid="live-user-draft">
            <div className="max-w-[75%] rounded-xl border border-dashed border-red-500/40 bg-red-950/20 px-3.5 py-2">
              <div className="mb-0.5 flex items-center gap-1 text-[10px] uppercase tracking-wider text-red-300"><Eye size={11} /> Élő gépelés</div>
              <div className="whitespace-pre-wrap text-[13px] italic text-zinc-300">{c.user_draft}<span className="ml-0.5 inline-block h-3.5 w-1.5 animate-pulse bg-red-400 align-middle" /></div>
            </div>
          </div>
        )}
      </div>
      <div className="border-t border-red-900/40 bg-[#0b0405] p-3">
        {c.user_typing && <div className="mb-2"><TypingDots label="A felhasználó ír" /></div>}
        {open ? <LiveComposer onSend={send} onType={onType} placeholder="Üzenet a felhasználónak…" sendLabel="Küldés" />
          : <p className="text-center text-[12px] text-zinc-500" data-testid="live-closed-note">A beszélgetés lezárva — a felhasználó már nem látja.</p>}
      </div>
    </div>
  );
}

export function ModLiveChats() {
  const [list, setList] = useState([]);
  const [sel, setSel] = useState(null);
  const [startOpen, setStartOpen] = useState(false);
  const load = () => api.get("/mod/livechats").then((r) => setList(r.data)).catch(() => {});
  useEffect(() => { load(); const t = setInterval(load, 4000); return () => clearInterval(t); }, []);

  return (
    <div className="flex h-[calc(100vh-8rem)] min-h-[460px] overflow-hidden rounded-lg border border-red-900/50 bg-[#0b0405]" data-testid="mod-live-chats">
      <div className="flex w-72 shrink-0 flex-col border-r border-red-900/40">
        <div className="flex items-center gap-2 border-b border-red-900/40 px-3 py-3">
          <Siren size={15} className="text-red-500" />
          <span className="text-[12px] font-bold uppercase tracking-[0.2em] text-zinc-200">Emergency line</span>
          <button data-testid="live-start-btn" onClick={() => setStartOpen(true)} className="ml-auto grid h-7 w-7 place-items-center rounded-md bg-red-600 text-white hover:bg-red-500"><Plus size={14} /></button>
        </div>
        <div className="flex-1 overflow-y-auto">
          {list.length === 0 && <p className="p-4 text-[12px] text-zinc-600">Nincs beszélgetés.</p>}
          {list.map((c) => (
            <button key={c.id} data-testid={`live-item-${c.id}`} onClick={() => setSel(c.id)}
              className={`block w-full border-b border-red-900/20 px-3 py-2.5 text-left transition-colors ${sel === c.id ? "bg-red-950/40" : "hover:bg-red-950/20"}`}>
              <div className="flex items-center gap-2">
                <span className={`h-2 w-2 rounded-full ${c.status === "open" ? (c.user_online ? "bg-red-500 shadow-[0_0_8px_#ef4444]" : "bg-red-900") : "bg-zinc-700"}`} />
                <span className="truncate text-[13px] text-zinc-200">{c.email}</span>
                {c.unread > 0 && <span data-testid={`live-unread-count-${c.id}`} className="ml-auto rounded-full bg-red-600 px-1.5 text-[10px] font-bold text-white">{c.unread}</span>}
              </div>
              <div className="mt-0.5 pl-4 text-[11px] text-zinc-500">{c.report_reference || "—"} · {c.status === "open" ? (c.user_typing ? "ír…" : "nyitott") : "lezárva"}</div>
            </button>
          ))}
        </div>
      </div>
      {sel ? <ChatPanel key={sel} id={sel} onChanged={load} /> : <div className="grid flex-1 place-items-center text-[13px] text-zinc-600">Válassz beszélgetést, vagy indíts újat.</div>}
      <StartDialog open={startOpen} onClose={() => setStartOpen(false)} onCreated={(c) => { load(); setSel(c.id); }} />
    </div>
  );
}
